import {memo, useCallback, useMemo} from 'react'
import type {MouseEvent} from 'react'
import {useLocation} from 'react-router-dom'

import {getHrefWithQueryParams} from '../../../../../../../routes'
import RouterLink from '../../../../../../common/RouterLink/RouterLink'
import {TestScopesKey} from '../../../../../../packages/Tests/Tests.types'
import type {TestScopeType} from '../../../../../../packages/Tests/Tests.types'
import {getTestScope, trimAndRemoveColon} from '../../../../../../packages/Tests/Tests.utils'
import {ViewType} from '../../../TestsFilter/TestsViewSelect/TestsViewSelect.utils'

import styles from './TestScopeName.css'

type Props = {
  scopeKey: TestScopesKey
  scope: TestScopeType
}

function TestScopeName({scopeKey, scope}: Props) {
  const location = useLocation()
  const links = useMemo(
    () => ({
      suite: getHrefWithQueryParams(location, null, prevParams => ({
        ...prevParams,
        ...getTestScope(TestScopesKey.SUITE, scope),
        view: ViewType.PACKAGES,
      })),
      package: getHrefWithQueryParams(location, null, prevParams => ({
        ...prevParams,
        ...getTestScope(TestScopesKey.PACKAGE, scope),
        view: ViewType.CLASSES,
      })),
      class: getHrefWithQueryParams(location, null, prevParams => ({
        ...prevParams,
        ...getTestScope(TestScopesKey.CLASS, scope),
        view: ViewType.TESTS,
        orderBy: null,
        order: null,
      })),
    }),
    [location, scope],
  )
  const onClick = useCallback((event: MouseEvent<any>) => {
    const selection = window.getSelection()
    if (selection != null && selection.toString().length > 0) {
      event.stopPropagation()
      event.preventDefault()
    }
  }, [])
  const renderLink = useCallback(
    (key: TestScopesKey) => {
      const scopeValue = scope[key] ?? ''
      return (
        <RouterLink className={styles[key]} to={links[key]} onClick={onClick} draggable="false">
          {scopeValue !== '' ? trimAndRemoveColon(scopeValue) : `<no ${key}>`}
        </RouterLink>
      )
    },
    [scope, links, onClick],
  )
  const renderPath = useMemo(
    () => (
      <>
        {scopeKey !== TestScopesKey.SUITE && Boolean(scope.suite)
          ? renderLink(TestScopesKey.SUITE)
          : null}
        {scopeKey !== TestScopesKey.PACKAGE && Boolean(scope.package)
          ? renderLink(TestScopesKey.PACKAGE)
          : null}
      </>
    ),
    [renderLink, scopeKey, scope.package, scope.suite],
  )
  return (
    <div className={styles.name}>
      {renderLink(scopeKey)}
      <div className={styles.path}>{renderPath}</div>
    </div>
  )
}

export default memo<Props>(TestScopeName)
